/**
 * Geo-based language detection for the public landing page.
 *
 * Why: browser/OS language (navigator.language) is a poor proxy for which
 * language a visitor understands — e.g. a visitor in India with the OS set
 * to Hindi has no "fr"/"en"/"ar" match, and previously fell back straight to
 * French (fallbackLng), producing a landing page the visitor couldn't read.
 *
 * Cloudflare (mawered.com is proxied through it) exposes the visitor's
 * country for free, with no API key or rate limit, via the standard
 * `/cdn-cgi/trace` endpoint present on every Cloudflare zone. We use that
 * instead of a third-party geo-IP service.
 */

// Gulf Cooperation Council — Arabic.
const GULF_COUNTRIES = new Set(["SA", "AE", "QA", "KW", "BH", "OM"]);

// Francophone countries — France/Europe francophone, Mawered's Maghreb
// market (TN/MA/DZ, French in business use), Canada, and Francophone Africa.
const FRANCOPHONE_COUNTRIES = new Set([
  "FR", "BE", "CH", "LU", "MC", "CA",
  "TN", "MA", "DZ",
  "SN", "CI", "CM", "ML", "BF", "NE", "TG", "BJ", "GA", "CD", "CG", "MG", "GN", "MR",
]);

export function countryToLanguage(countryCode) {
  const cc = (countryCode || "").toUpperCase();
  if (GULF_COUNTRIES.has(cc)) return "ar";
  if (FRANCOPHONE_COUNTRIES.has(cc)) return "fr";
  return "en";
}

/**
 * Resolves the visitor's country via Cloudflare's trace endpoint.
 * Returns null on any failure (network, timeout, not behind Cloudflare —
 * e.g. local dev) so callers can fall back to the existing detection chain.
 */
export async function detectGeoLanguage({ timeoutMs = 2500 } = {}) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch("/cdn-cgi/trace", { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const text = await res.text();
    const match = text.match(/^loc=([A-Z]{2})$/m);
    if (!match) return null;
    return countryToLanguage(match[1]);
  } catch (_) {
    return null;
  }
}
